/**
 * On a collection page (/collections/<handle>), product links keep the
 * collection in the URL: /collections/<handle>/products/<product>.
 */
function srCollectionProductUrl(url) {
  if (!url) return url;
  const match = window.location.pathname.match(/^(\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^\/]+)(?:\/|$)/i);
  if (!match || /\/collections\//.test(url)) return url;
  return url.replace(/^(\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?)?products\//i, match[1] + '/products/');
}

if (!customElements.get('sr-product-card-new')) {

class SrProductCardNew extends HTMLElement {
  connectedCallback() {
    this.card = this.querySelector(".product-card");
    if (!this.card) return;

    // Keep the collection in product links: /collections/<handle>/products/<product>
    this.card.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      const updated = srCollectionProductUrl(href);
      if (updated !== href) link.setAttribute('href', updated);
    });

    this.slidesEl = this.card.querySelector(".slides");
    this.slidesParent = this.slidesEl.parentElement;
    this.dotsContainer = this.card.querySelector(".dots");

    this.index = 0;
    this.startX = 0;
    this.deltaX = 0;
    this.isPointerDown = false;
    this.wasDragging = false;

    this.DRAG_THRESHOLD = 50;
    this.CLICK_CANCEL_THRESHOLD = 6;

    this.init();
  }

  // -----------------------------
  // Init
  // -----------------------------
  init() {

    this.createDots(this.slidesEl.children.length);
    this.animateToIndex();

    this.bindSwipeEvents();
    if (this.dataset.hasSwatches === "true") this.bindSwatchEvents();

    window.addEventListener("resize", () => this.animateToIndex());

    if (this.dataset.hasUrl === "true" && this.dataset.url) {
      this.style.cursor = "pointer";

      const redirect = (e) => {
        // ignore drag
        if (Math.abs(this.deltaX) > this.CLICK_CANCEL_THRESHOLD) return;
        // ignore clicks on links/buttons
        if (e.target.closest("a") || e.target.closest("button")) return;
        // redirect
        window.location.href = this.dataset.url;
      };

      // Use pointerup on slidesParent and also on card for areas outside slides
      this.slidesParent.addEventListener("pointerup", redirect);
      this.card.addEventListener("pointerup", redirect);
    }
  }

  // -----------------------------
  // Dots
  // -----------------------------
  createDots(count) {
    this.dotsContainer.innerHTML = "";
    if (count <= 1) {
      this.dotsContainer.style.display = "none";
      return;
    } else {
      this.dotsContainer.style.display = "";
    }
    for (let i = 0; i < count; i++) {
      const dot = document.createElement("div");
      dot.className = "dot" + (i === 0 ? " active" : "");
      dot.setAttribute("role", "button");
      dot.setAttribute("tabindex", "0");
      dot.setAttribute("aria-label", `Show image ${i + 1} of ${count}`);
      dot.addEventListener("click", () => {
        this.index = i;
        this.animateToIndex();
      });
      dot.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.index = i;
          this.animateToIndex();
        }
      });
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    const dots = this.dotsContainer.querySelectorAll(".dot");
    dots.forEach(d => d.classList.remove("active"));
    dots.forEach(d => d.removeAttribute("aria-current"));
    dots[this.index]?.classList.add("active");
    dots[this.index]?.setAttribute("aria-current", "true");
  }

  // -----------------------------
  // Slider Movement
  // -----------------------------
  animateToIndex() {
    this.slidesEl.style.transition = "transform 0.4s ease-in-out";
    const width = this.slidesParent.clientWidth;
    this.slidesEl.style.transform = `translateX(${-this.index * width}px)`;
    this.updateDots();
  }

  // -----------------------------
  // Swipe Handlers
  // -----------------------------
  bindSwipeEvents() {
    const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

    const onPointerDown = e => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      this.isPointerDown = true;
      this.wasDragging = false;
      this.startX = e.clientX;
      this.deltaX = 0;
      this.slidesEl.style.transition = "none";
    };

    const onPointerMove = e => {
      if (!this.isPointerDown) return;

      const width = this.slidesParent.clientWidth;
      const maxIndex = this.slidesEl.children.length - 1;
      this.deltaX = e.clientX - this.startX;

      // Edge resistance
      let offsetX = -this.index * width + this.deltaX;
      if ((this.index === 0 && this.deltaX > 0) || (this.index === maxIndex && this.deltaX < 0)) {
        // Resistance effect when trying to drag beyond edges
        offsetX = -this.index * width + this.deltaX * 0.3;
      }

      this.slidesEl.style.transform = `translateX(${offsetX}px)`;

      if (Math.abs(this.deltaX) > this.CLICK_CANCEL_THRESHOLD) this.wasDragging = true;
    };

    const onPointerUp = () => {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      const width = this.slidesParent.clientWidth;
      const maxIndex = this.slidesEl.children.length - 1;

      // Decide new index
      if (Math.abs(this.deltaX) > this.DRAG_THRESHOLD) {
        if (this.deltaX < 0 && this.index < maxIndex) {
          this.index++;
        } else if (this.deltaX > 0 && this.index > 0) {
          this.index--;
        }
      }

      // Snap back to valid position
      this.animateToIndex();

      // Prevent click after drag
      if (this.wasDragging) {
        this.slidesParent.querySelectorAll("a").forEach(a => {
          a.addEventListener("click", ev => ev.preventDefault(), { once: true });
        });
      }
    };

    const onPointerCancel = () => {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      this.animateToIndex(); // Snap back cleanly
    };

    // Bind events
    this.slidesParent.addEventListener("pointerdown", onPointerDown);
    this.slidesParent.addEventListener("pointermove", onPointerMove);
    this.slidesParent.addEventListener("pointerup", onPointerUp);
    this.slidesParent.addEventListener("pointercancel", onPointerCancel);

    // Optional: release if user leaves container
    this.slidesParent.addEventListener("pointerleave", () => {
      if (this.isPointerDown) onPointerUp();
    });
    const onTouchStart = (e) => {
  this.isPointerDown = true;
  this.startX = e.touches[0].clientX;
  this.startY = e.touches[0].clientY;
  this.deltaX = 0;
  this.isHorizontal = false;

  this.slidesEl.style.transition = "none";
};

const onTouchMove = (e) => {
  if (!this.isPointerDown) return;

  const touch = e.touches[0];
  const diffX = touch.clientX - this.startX;
  const diffY = touch.clientY - this.startY;

  // Detect horizontal intent
  if (!this.isHorizontal) {
    this.isHorizontal = Math.abs(diffX) > Math.abs(diffY);
  }

  // ❗ IMPORTANT: stop page scroll ONLY if horizontal
  if (this.isHorizontal) {
    e.preventDefault(); // THIS is the key fix
  } else {
    return; // allow vertical scroll
  }

  this.deltaX = diffX;

  const width = this.slidesParent.clientWidth;
  const maxIndex = this.slidesEl.children.length - 1;

  let offsetX = -this.index * width + this.deltaX;

  // Edge resistance
  if (
    (this.index === 0 && this.deltaX > 0) ||
    (this.index === maxIndex && this.deltaX < 0)
  ) {
    offsetX = -this.index * width + this.deltaX * 0.3;
  }

  this.slidesEl.style.transform = `translateX(${offsetX}px)`;
};

const onTouchEnd = () => {
  if (!this.isPointerDown) return;

  this.isPointerDown = false;

  const maxIndex = this.slidesEl.children.length - 1;

  if (Math.abs(this.deltaX) > this.DRAG_THRESHOLD) {
    if (this.deltaX < 0 && this.index < maxIndex) {
      this.index++;
    } else if (this.deltaX > 0 && this.index > 0) {
      this.index--;
    }
  }

  this.animateToIndex();
};
this.slidesParent.addEventListener("touchstart", onTouchStart, { passive: true });
this.slidesParent.addEventListener("touchmove", onTouchMove, { passive: false }); // ❗ MUST be false
this.slidesParent.addEventListener("touchend", onTouchEnd);
  }

  // -----------------------------
  // Swatches
  // -----------------------------
  bindSwatchEvents() {
    this.querySelectorAll(".color-swatches").forEach(colorDot => {
      colorDot.addEventListener("change", e => {
        if (!e.target.matches('input[type="radio"]')) return;
        if (!products) return;
        this.index = 0;
        this.animateToIndex();
        const productId = e.target.value;
        const productData = products[productId];
        if (!productData) return;
        
        const quickBuyBtn = this.querySelector(".sr-quick-buy-btn");
        if (quickBuyBtn) {
          quickBuyBtn.href = productData.url;
          quickBuyBtn.dataset.id = productId;
          quickBuyBtn.dataset.handle = productData.handle;
          quickBuyBtn.dataset.productType = productData.type;
          quickBuyBtn.dataset.url = srCollectionProductUrl(productData.url);
        }

        const content_product_info = e.target.closest(".sr-colordots")
        const product_info_top = this.querySelector(".product-card-info-top");
        const product_tags = this.querySelector(".sr-product-tag");
        
        if (product_info_top && content_product_info) {
          product_info_top.innerHTML = '';
          product_info_top.innerHTML = content_product_info.querySelector(".product-card-info-top-tmp").innerHTML;
        }
       if (product_tags && content_product_info) {
          product_tags.innerHTML = '';
          product_tags.innerHTML = content_product_info.querySelector(".product-card-info-tag-tmp .sr-product-tag").innerHTML;
        }
        const product_info_bottom = this.querySelector(".product-card-info-bottom");
        if (product_info_bottom && content_product_info) {
          product_info_bottom.innerHTML = '';
          product_info_bottom.innerHTML = content_product_info.querySelector(".product-card-info-bottom-tmp").innerHTML;
        }

        if (window.yotpo && typeof window.yotpo.refreshWidgets === "function") {
          window.yotpo.refreshWidgets();
        }


        const productImgs = productData.images || [];
        
        if (productImgs.length === 0) return;
        this.createDots(productImgs.length);
        // rebuild slides
        this.slidesEl.innerHTML = "";
        productImgs.forEach(imgData => {
          const slide = document.createElement("a");
          slide.href = srCollectionProductUrl(productData.url);
          slide.innerHTML = `
            <img src="${imgData.src}"
                 srcset="${imgData.srcset}"
                 alt="${imgData.alt || productData.title || ''}"
                 loading="lazy"
                 class="default-image" />
          `;
          this.slidesEl.appendChild(slide);
        });

        // if (typeof yotpoWidgetsContainer !== 'undefined') {
        //   yotpoWidgetsContainer.initWidgets();
        // }
      });
    });
  }
}

customElements.define("sr-product-card-new", SrProductCardNew);
}


/**
 * For React custom product card
 */

function initReactColorDots() {
    document.addEventListener('change', function (event) {
        const input = event.target.closest(
            '.react-color-dot input[type="radio"]'
        );

        if (!input) return;

        const colorDot = input.closest('.react-color-dot');
        const card = input.closest('sr-product-card-new');

        if (!colorDot || !card) return;

        const productUrl = srCollectionProductUrl(
            `${input.dataset.prodUrl}` +
            `?variant=${input.dataset.frameVariant}` +
            `&lens=${input.dataset.lensVariant}`
        );

        const primaryImage = card.querySelector(
            '[data-index="1"] img'
        );

        const secondaryImage = card.querySelector(
            '[data-index="2"] img'
        );

        if (primaryImage && input.dataset.img1) {
            primaryImage.src = input.dataset.img1;
            primaryImage.removeAttribute('srcset');
        }

        if (secondaryImage && input.dataset.img2) {
            secondaryImage.src = input.dataset.img2;
            secondaryImage.removeAttribute('srcset');
        }

        card.querySelectorAll(
            '[data-index="1"], [data-index="2"]'
        ).forEach((link) => {
            link.href = productUrl;
        });

        const tempInfo = colorDot.querySelector(
            '.product-card-info-top-tmp'
        );

        const cardInfo = card.querySelector(
            '.product-card-info-top'
        );

        if (tempInfo && cardInfo) {
            cardInfo.innerHTML = tempInfo.innerHTML;
        }

        const productLink = card.querySelector(
            '.react-product-link'
        );

        if (productLink) {
            productLink.href = productUrl;
        }
    });
}

initReactColorDots();